import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { AuthService } from './modules/shared/services/auth/auth.service';
import { AuthEventsService, AuthEventType } from './modules/shared/services/auth/auth-events.service';
import { UserRoleService } from './modules/shared/services/user-role/user-role.service';
import { OrganizationGroupService } from './modules/shared/services/organization-group/organization-group.service';
import {
  InitialContextResolverService,
  PickerConfig,
} from './modules/shared/services/initial-context-resolver/initial-context-resolver.service';
import { Organization } from './modules/shared/services/users/users.service';
import { ContextPickerResult } from './modules/shared/components/firm-context-picker/firm-context-picker.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'saffron-ui';

  /**
   * Gates the router-outlet until initial org context is resolved.
   * Prevents data fetches with wrong org scope on first render.
   * Immediately true for roles that don't require context (ADVISOR, MANAGER).
   */
  contextReady = false;

  /** Non-null while the firm-context-picker modal should be displayed. */
  pickerConfig: PickerConfig | null = null;

  /** Controls visibility of the firm picker modal from the side nav. */
  firmPickerOpen = false;

  private readonly destroy$ = new Subject<void>();
  private resolving = false;

  constructor(
    private authService: AuthService,
    private authEventsService: AuthEventsService,
    private userRoleService: UserRoleService,
    private organizationGroupService: OrganizationGroupService,
    private initialContextResolver: InitialContextResolverService,
  ) {}

  ngOnInit(): void {
    // Not authenticated on startup → show login page immediately.
    if (!this.authService.isAuthenticated()) {
      this.contextReady = true;
    }

    this.authEventsService.authEvents$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        switch (event.type) {
          case AuthEventType.USER_DATA_LOADED:
            this.resolveContext();
            break;
          case AuthEventType.LOGGED_OUT:
          case AuthEventType.SESSION_EXPIRED:
            this.contextReady = false;
            this.initialContextResolver.dismissPicker();
            this.pickerConfig = null;
            break;
        }
      });

    this.initialContextResolver.showPicker$
      .pipe(takeUntil(this.destroy$))
      .subscribe((config) => {
        this.pickerConfig = config;
      });

    // Handle group changes from inside the picker — load new orgs and update picker.
    this.initialContextResolver.pickerGroupChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe((groupId) => {
        this.organizationGroupService
          .listGroupOrganizationsForSwitcher(groupId, 1, 100)
          .pipe(take(1), takeUntil(this.destroy$))
          .subscribe({
            next: (result) => {
              const sorted: Organization[] = [...result.data].sort((a, b) =>
                a.ORGANIZATION_NAME.localeCompare(b.ORGANIZATION_NAME),
              );
              this.initialContextResolver.updatePickerOrgs(sorted);
            },
            error: (err: unknown) => {
              console.error('AppComponent: failed to load orgs for picker group', err);
            },
          });
      });

    // Edge case: user data was already loaded before this component's ngOnInit ran.
    if (this.authEventsService.isUserDataLoaded()) {
      this.resolveContext();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onPickerGroupChange(groupId: number): void {
    this.initialContextResolver.onPickerGroupChange(groupId);
  }

  onPickerContextSelected(result: ContextPickerResult): void {
    this.initialContextResolver.onPickerConfirmed(result);
  }

  private resolveContext(): void {
    if (this.resolving || this.contextReady) return;
    this.resolving = true;

    const role = this.userRoleService.getCurrentRole();
    this.initialContextResolver
      .resolve(role)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.resolving = false;
        }),
      )
      .subscribe({
        next: () => {
          this.contextReady = true;
        },
        error: (err: unknown) => {
          console.error('AppComponent: context resolution failed, proceeding anyway', err);
          this.contextReady = true;
        },
      });
  }
}
